// ==============================================
// MyPolaris — chart editing for routes and waypoints
// ==============================================
//
// The gestures here deliberately mirror VortexNav's chart, because a skipper
// who learned to edit a route on the Scout should not have to learn a second
// way of doing it at a desk. Cross-checked against the app's own source:
//
//   vortexnav-app/src/components/MapView.tsx          the three context menus
//   vortexnav-app/src/components/RouteCreationOverlay.tsx  the placement overlay
//   vortexnav-app/src/hooks/useRouteManager.ts        extend / remove / insert
//   vortexnav-app/src/utils/routeWaypointNaming.ts    inserted-waypoint names
//
// VN is MapLibre GL and this is Leaflet, so the hit-testing and drag layer is
// written for Leaflet rather than lifted — but the behaviour it produces, and
// the wording the user reads, are the same.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { PencilIcon } from '@heroicons/react/24/outline';
import {
  type EditPoint, type LatLon, type History,
  bearingDeg, distanceNm, totalNm,
  insertedWaypointName, nextExtendName, drawnWaypointName,
  emptyHistory, pushHistory, undo as undoHistory, redo as redoHistory,
  thinToTarget, THIN_TARGET_MIN, THIN_TARGET_MAX, THIN_TARGET_DEFAULT,
} from '../../utils/navEdit';
import type { NavElement } from '../../types/nav';

const ROUTE_COLOR = '#3987e5';
const WPT_COLOR = '#f0c25a';

export type EditMode = 'off' | 'edit' | 'draw-route' | 'draw-waypoint' | 'extend-start' | 'extend-end';

// ---------------------------------------------------------------- state

export interface ChartEditorState {
  mode: EditMode;
  /** The element being edited, or null while drawing something new. */
  target: NavElement | null;
  points: EditPoint[];
  /** Points placed so far in a draw/extend pass, before they join the route. */
  placing: EditPoint[];
  drawName: string;
  dirty: boolean;
  selected: number;
}

const IDLE: ChartEditorState = {
  mode: 'off', target: null, points: [], placing: [], drawName: '', dirty: false, selected: -1,
};

/**
 * Editing lives in one hook so the page stays a page. Undo/redo covers the
 * whole session and dies with it — Cancel throws the session away rather than
 * unwinding it, which is what was agreed.
 */
export function useChartEditor(opts: {
  onSave: (points: EditPoint[]) => Promise<void>;
  onSaveAs: (name: string, points: EditPoint[], sourceId: string | null) => Promise<void>;
  onDraw: (type: 'route' | 'waypoint', name: string, points: EditPoint[], sourceId: string | null) => Promise<void>;
}) {
  const [s, setS] = useState<ChartEditorState>(IDLE);
  const [history, setHistory] = useState<History<EditPoint[]>>(emptyHistory<EditPoint[]>());
  const baseline = useRef<EditPoint[]>([]);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const isEditing = s.mode !== 'off';
  const isPlacing = s.mode === 'draw-route' || s.mode === 'draw-waypoint' || s.mode === 'extend-start' || s.mode === 'extend-end';

  const same = (a: EditPoint[], b: EditPoint[]) => JSON.stringify(a) === JSON.stringify(b);

  // Current points, readable outside a state updater. Updaters must stay pure —
  // calling setHistory from inside one double-pushes under StrictMode, which
  // made a single gesture cost two presses of undo.
  const pointsRef = useRef<EditPoint[]>(s.points);
  pointsRef.current = s.points;

  const commit = useCallback((next: EditPoint[]) => {
    pointsRef.current = next;
    setS((prev) => ({ ...prev, points: next, dirty: !same(next, baseline.current) }));
  }, []);

  /** Take an undo snapshot, then apply a change. One step per gesture. */
  const mutate = useCallback((fn: (pts: EditPoint[]) => EditPoint[]) => {
    const prev = pointsRef.current;
    setHistory((h) => pushHistory(h, prev));
    commit(fn(prev));
  }, [commit]);

  /** Snapshot without changing anything — used before a drag begins. */
  const snapshot = useCallback(() => {
    setHistory((h) => pushHistory(h, pointsRef.current));
  }, []);

  /** Apply without adding an undo step (the snapshot already ran). */
  const applyLive = useCallback((fn: (pts: EditPoint[]) => EditPoint[]) => {
    commit(fn(pointsRef.current));
  }, [commit]);

  const begin = useCallback((el: NavElement, points: EditPoint[]) => {
    baseline.current = points;
    pointsRef.current = points;
    setHistory(emptyHistory<EditPoint[]>());
    setS({ ...IDLE, mode: 'edit', target: el, points, dirty: false, selected: -1 });
  }, []);

  const startDraw = useCallback((type: 'route' | 'waypoint') => {
    baseline.current = [];
    setHistory(emptyHistory<EditPoint[]>());
    setS({ ...IDLE, mode: type === 'route' ? 'draw-route' : 'draw-waypoint', drawName: '' });
  }, []);

  const startExtend = useCallback((end: 'start' | 'end') => {
    setS((prev) => (prev.mode === 'edit'
      ? { ...prev, mode: end === 'start' ? 'extend-start' : 'extend-end', placing: [] }
      : prev));
  }, []);

  const cancel = useCallback(() => {
    baseline.current = [];
    setHistory(emptyHistory<EditPoint[]>());
    setS(IDLE);
  }, []);

  /** Leave a placement pass without leaving the edit session. */
  const cancelPlacing = useCallback(() => {
    setS((prev) => (prev.target
      ? { ...prev, mode: 'edit', placing: [] }
      : IDLE));
  }, []);

  const undo = useCallback(() => {
    const step = undoHistory(history, pointsRef.current);
    if (!step) return;
    setHistory(step.history);
    pointsRef.current = step.value;
    setS((prev) => ({ ...prev, points: step.value, selected: -1, dirty: !same(step.value, baseline.current) }));
  }, [history]);

  const redo = useCallback(() => {
    const step = redoHistory(history, pointsRef.current);
    if (!step) return;
    setHistory(step.history);
    pointsRef.current = step.value;
    setS((prev) => ({ ...prev, points: step.value, selected: -1, dirty: !same(step.value, baseline.current) }));
  }, [history]);

  // ---- the operations themselves ----

  const movePoint = useCallback((i: number, lat: number, lon: number) => {
    applyLive((pts) => pts.map((p, n) => (n === i ? { ...p, lat, lon } : p)));
  }, [applyLive]);

  const insertPoint = useCallback((index: number, lat: number, lon: number) => {
    mutate((pts) => {
      const prev = pts[index - 1];
      const next = pts[index] ?? null;
      const name = prev
        ? insertedWaypointName(prev.name, next?.name ?? null, pts.map((p) => p.name))
        : insertedWaypointName(next?.name ?? 'WP', null, pts.map((p) => p.name));
      return [...pts.slice(0, index), { lat, lon, name }, ...pts.slice(index)];
    });
  }, [mutate]);

  const removePoint = useCallback((i: number) => {
    mutate((pts) => pts.filter((_, n) => n !== i));
  }, [mutate]);

  const renamePoint = useCallback((i: number, name: string) => {
    mutate((pts) => pts.map((p, n) => (n === i ? { ...p, name } : p)));
  }, [mutate]);

  const reverse = useCallback(() => mutate((pts) => [...pts].reverse()), [mutate]);

  const select = useCallback((i: number) => setS((prev) => ({ ...prev, selected: i })), []);
  const setDrawName = useCallback((drawName: string) => setS((prev) => ({ ...prev, drawName })), []);

  /** Place one point during a draw or extend pass. */
  const place = useCallback((lat: number, lon: number) => {
    setS((prev) => {
      if (prev.mode === 'draw-waypoint') {
        return { ...prev, placing: [{ lat, lon, name: prev.drawName || 'New waypoint' }] };
      }
      const name = prev.mode === 'draw-route'
        ? drawnWaypointName(prev.drawName || 'New Route', prev.placing.length)
        : nextExtendName(prev.target?.name ?? 'Route', [...prev.points, ...prev.placing].map((p) => p.name));
      return { ...prev, placing: [...prev.placing, { lat, lon, name }] };
    });
  }, []);

  const undoPlace = useCallback(() => {
    setS((prev) => ({ ...prev, placing: prev.placing.slice(0, -1) }));
  }, []);

  /** Finish a placement pass: either commit to the route, or create outright. */
  const finishPlacing = useCallback(async () => {
    const cur = s;
    if (cur.mode === 'extend-start' || cur.mode === 'extend-end') {
      const added = cur.mode === 'extend-start' ? [...cur.placing].reverse() : cur.placing;
      setHistory((h) => pushHistory(h, cur.points));
      const next = cur.mode === 'extend-start' ? [...added, ...cur.points] : [...cur.points, ...added];
      setS((prev) => ({ ...prev, mode: 'edit', placing: [], points: next, dirty: true }));
      return;
    }
    const type = cur.mode === 'draw-waypoint' ? 'waypoint' : 'route';
    const name = cur.drawName.trim() || (type === 'route' ? 'New Route' : 'New waypoint');
    // A route's points were named while the route was still unnamed; restate
    // them from the final name so "New Route - WP1" is not left behind.
    const pts = type === 'route'
      ? cur.placing.map((p, i) => ({ ...p, name: drawnWaypointName(name, i) }))
      : cur.placing.map((p) => ({ ...p, name }));
    await optsRef.current.onDraw(type, name, pts, null);
    cancel();
  }, [s, cancel]);

  /**
   * Save closes the session, exactly as Save As does and as VN's own
   * saveRoute → closeEdit does.
   *
   * It used to stay open with Save greyed out, which stranded the user: the
   * New route and Edit on chart buttons stand down during an edit, so the only
   * way out was Cancel — a button that reads as "throw away my work" moments
   * after saving it. Committing and closing is the honest end of the gesture.
   */
  const save = useCallback(async () => {
    await optsRef.current.onSave(pointsRef.current);
    cancel();
  }, [cancel]);

  const saveAs = useCallback(async (name: string) => {
    await optsRef.current.onSaveAs(name, pointsRef.current, s.target?.id ?? null);
    cancel();
  }, [s.target, cancel]);

  const minPlaced = s.mode === 'draw-route' ? 2 : 1;
  const stats = useMemo(() => {
    const pts = isPlacing ? s.placing : s.points;
    return { count: pts.length, nm: totalNm(pts) };
  }, [isPlacing, s.placing, s.points]);

  return {
    state: s, isEditing, isPlacing, stats,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    canFinishPlacing: s.placing.length >= minPlaced,
    begin, startDraw, startExtend, cancel, cancelPlacing,
    undo, redo, snapshot,
    movePoint, insertPoint, removePoint, renamePoint, reverse, select,
    place, undoPlace, finishPlacing, setDrawName,
    save, saveAs,
  };
}

export type ChartEditor = ReturnType<typeof useChartEditor>;

// ---------------------------------------------------------------- icons

// react-leaflet's updateMarker compares `icon` by REFERENCE and calls
// marker.setIcon() when it differs — which tears down and rebuilds the marker's
// DOM element. Handing it a fresh divIcon every render therefore destroyed the
// element mid-drag and the waypoint barely moved. Icons are cached so an
// unchanged marker keeps the identical instance across renders. Sharing one
// DivIcon between markers is safe: Leaflet builds a new element per marker from
// the same definition.
const iconCache = new Map<string, L.DivIcon>();

export const waypointIcon = (label: string, selected: boolean, dim = false): L.DivIcon => {
  const key = JSON.stringify([label, selected, dim]);
  const hit = iconCache.get(key);
  if (hit) return hit;
  const icon = L.divIcon({
    className: 'nav-edit-wpt',
    html: `<div style="display:flex;align-items:center;gap:6px;transform:translate(-9px,-9px)">
      <span style="width:18px;height:18px;border-radius:50%;background:${WPT_COLOR};
        border:${selected ? '3px solid #fff' : '2px solid #0a1220'};box-shadow:0 0 0 1px rgba(0,0,0,.6);
        opacity:${dim ? 0.65 : 1};flex:none"></span>
      <span style="background:rgba(10,18,32,.88);color:#e8f1fa;font:600 11px/1.4 system-ui,sans-serif;
        padding:2px 6px;border-radius:4px;white-space:nowrap;border:1px solid rgba(255,255,255,.15)"
        >${escapeHtml(label)}</span>
    </div>`,
    iconSize: [0, 0],
  });
  // Renaming points over a long session would otherwise grow this without end.
  if (iconCache.size > 600) iconCache.clear();
  iconCache.set(key, icon);
  return icon;
};

const MIDPOINT_ICON = L.divIcon({
  className: 'nav-edit-mid',
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#0a1220;border:2px solid #7cc4ff;
      display:flex;align-items:center;justify-content:center;transform:translate(-11px,-11px);cursor:copy">
      <span style="color:#7cc4ff;font:700 15px/1 system-ui,sans-serif">+</span></div>`,
  iconSize: [0, 0],
});
const midpointIcon = () => MIDPOINT_ICON;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

// ---------------------------------------------------------------- layer

export interface MenuRequest {
  kind: 'chart' | 'waypoint' | 'leg';
  x: number;
  y: number;
  lat: number;
  lon: number;
  index: number;
}

/**
 * The editable geometry itself. Rendered inside the MapContainer.
 *
 * Waypoints are Markers rather than CircleMarkers because only Markers drag,
 * and dragging is the gesture this whole feature exists for.
 */
export function ChartEditLayer({ editor, onMenu }: { editor: ChartEditor; onMenu: (m: MenuRequest | null) => void }) {
  const map = useMap();
  const { state, isPlacing } = editor;
  const [hoverLeg, setHoverLeg] = useState(-1);
  // Where a waypoint currently sits under the cursor. Held apart from the
  // editor's points on purpose: react-leaflet compares `position` by reference
  // and calls marker.setLatLng() whenever it differs, which during a drag yanks
  // the marker back to its committed spot and fights Leaflet's own transform.
  // So the drag updates only the line, and the point is committed on dragend.
  const [dragAt, setDragAt] = useState<{ index: number; lat: number; lon: number } | null>(null);

  // One stable tuple per point. A fresh array literal per render would trip the
  // reference check above on every unrelated re-render.
  const markerPositions = useMemo(
    () => state.points.map((p) => [p.lat, p.lon] as [number, number]),
    [state.points],
  );

  const live = isPlacing && state.mode !== 'extend-start' && state.mode !== 'extend-end'
    ? state.placing
    : state.points;
  const placingTail = state.mode === 'extend-start' || state.mode === 'extend-end' ? state.placing : [];

  useMapEvents({
    click(e) {
      onMenu(null);
      if (isPlacing) editor.place(e.latlng.lat, e.latlng.lng);
    },
    contextmenu(e) {
      L.DomEvent.preventDefault(e.originalEvent as unknown as Event);
      if (isPlacing) return;
      onMenu({ kind: 'chart', x: e.containerPoint.x, y: e.containerPoint.y, lat: e.latlng.lat, lon: e.latlng.lng, index: -1 });
    },
    movestart() { onMenu(null); },
  });

  // Esc leaves a placement pass, matching VN's Cancel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onMenu(null); if (isPlacing) editor.cancelPlacing(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isPlacing, editor, onMenu]);

  const line: LatLon[] = useMemo(() => {
    const shown = dragAt
      ? live.map((p, i) => (i === dragAt.index ? { ...p, lat: dragAt.lat, lon: dragAt.lon } : p))
      : live;
    const all = state.mode === 'extend-start'
      ? [...[...placingTail].reverse(), ...shown]
      : [...shown, ...placingTail];
    return all.map((p) => [p.lat, p.lon] as LatLon);
  }, [live, placingTail, state.mode, dragAt]);

  const isRoute = state.target?.type === 'route' || state.mode === 'draw-route'
    || state.mode === 'extend-start' || state.mode === 'extend-end';

  return (
    <>
      {isRoute && line.length > 1 && (
        <Polyline positions={line} pathOptions={{ color: ROUTE_COLOR, weight: 3, opacity: 0.95 }} />
      )}

      {/* One invisible fat polyline per leg: the right-click and hover target. */}
      {!isPlacing && isRoute && state.points.slice(0, -1).map((p, i) => (
        <Polyline
          key={`hit-${i}`}
          positions={[
            dragAt?.index === i ? [dragAt.lat, dragAt.lon] : [p.lat, p.lon],
            dragAt?.index === i + 1
              ? [dragAt.lat, dragAt.lon]
              : [state.points[i + 1].lat, state.points[i + 1].lon],
          ] as LatLon[]}
          pathOptions={{ color: '#ffffff', weight: 20, opacity: 0 }}
          eventHandlers={{
            mouseover: () => setHoverLeg(i),
            mouseout: () => setHoverLeg((cur) => (cur === i ? -1 : cur)),
            contextmenu: (e) => {
              L.DomEvent.preventDefault(e.originalEvent as unknown as Event);
              L.DomEvent.stopPropagation(e.originalEvent as unknown as Event);
              onMenu({ kind: 'leg', x: e.containerPoint.x, y: e.containerPoint.y, lat: e.latlng.lat, lon: e.latlng.lng, index: i });
            },
          }}
        />
      ))}

      {/* Midpoint handle on the hovered leg — VN's "tap the midpoint of a leg". */}
      {!isPlacing && isRoute && hoverLeg >= 0 && state.points[hoverLeg + 1] && (
        <Marker
          position={[
            (state.points[hoverLeg].lat + state.points[hoverLeg + 1].lat) / 2,
            (state.points[hoverLeg].lon + state.points[hoverLeg + 1].lon) / 2,
          ]}
          icon={midpointIcon()}
          eventHandlers={{
            click: (e) => {
              L.DomEvent.stopPropagation(e.originalEvent as unknown as Event);
              const a = state.points[hoverLeg];
              const b = state.points[hoverLeg + 1];
              editor.insertPoint(hoverLeg + 1, (a.lat + b.lat) / 2, (a.lon + b.lon) / 2);
              setHoverLeg(-1);
            },
            mouseover: () => setHoverLeg(hoverLeg),
          }}
        />
      )}

      {/* Committed waypoints — draggable. */}
      {!isPlacing && state.points.map((p, i) => (
        <Marker
          key={`wp-${i}`}
          position={markerPositions[i]}
          draggable
          icon={waypointIcon(p.name, state.selected === i)}
          eventHandlers={{
            dragstart: () => { onMenu(null); editor.snapshot(); },
            drag: (e) => {
              const ll = (e.target as L.Marker).getLatLng();
              setDragAt({ index: i, lat: ll.lat, lon: ll.lng });
            },
            dragend: (e) => {
              const ll = (e.target as L.Marker).getLatLng();
              setDragAt(null);
              editor.movePoint(i, ll.lat, ll.lng);
            },
            click: (e) => { L.DomEvent.stopPropagation(e.originalEvent as unknown as Event); editor.select(i); },
            contextmenu: (e) => {
              L.DomEvent.preventDefault(e.originalEvent as unknown as Event);
              L.DomEvent.stopPropagation(e.originalEvent as unknown as Event);
              const pt = map.latLngToContainerPoint(e.latlng);
              onMenu({ kind: 'waypoint', x: pt.x, y: pt.y, lat: e.latlng.lat, lon: e.latlng.lng, index: i });
            },
          }}
        />
      ))}

      {/* Points placed in the current pass, not yet part of the route. */}
      {(isPlacing ? state.placing : []).map((p, i) => (
        <Marker key={`pl-${i}`} position={[p.lat, p.lon]} icon={waypointIcon(p.name, false, true)} />
      ))}
    </>
  );
}

// ---------------------------------------------------------------- menus

/**
 * The three context menus, worded exactly as VortexNav words them. Rendered as
 * a plain overlay rather than a Leaflet popup so it can sit above the map
 * controls and be dismissed the same way everywhere.
 */
export function ChartContextMenu({
  menu, editor, onClose, onRename,
}: {
  menu: MenuRequest;
  editor: ChartEditor;
  onClose: () => void;
  onRename: (index: number) => void;
}) {
  const { state } = editor;
  const pts = state.points;
  const canRemove = pts.length > 2;

  const Item = ({ icon, label, danger, disabled, onClick }: {
    icon: string; label: string; danger?: boolean; disabled?: boolean; onClick?: () => void;
  }) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => { if (!disabled) { onClose(); onClick?.(); } }}
      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm ${
        disabled ? 'cursor-not-allowed text-slate-500'
          : danger ? 'text-rose-300 hover:bg-white/10' : 'text-slate-100 hover:bg-white/10'
      }`}
    >
      <span className="w-4 text-center text-xs">{icon}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <div
      className="absolute z-[1200] min-w-[210px] rounded-lg border border-white/15 bg-[#12243a] p-1.5 shadow-2xl"
      style={{ left: Math.max(4, menu.x), top: Math.max(4, menu.y) }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {menu.kind === 'chart' && (
        <>
          <Item icon="📍" label="New Waypoint" onClick={() => editor.startDraw('waypoint')} />
          <Item icon="✏️" label="Add with details…" onClick={() => editor.startDraw('waypoint')} />
          <Item icon="🗺️" label="Add Route" onClick={() => editor.startDraw('route')} />
        </>
      )}

      {menu.kind === 'leg' && (
        <Item icon="➕" label="Insert waypoint here" onClick={() => editor.insertPoint(menu.index + 1, menu.lat, menu.lon)} />
      )}

      {menu.kind === 'waypoint' && (
        <>
          <Item icon="✏️" label="Edit waypoint" onClick={() => onRename(menu.index)} />
          {state.target?.type === 'route' && (
            <>
              <div className="my-1 border-t border-white/10" />
              <div className="px-2.5 pb-1 pt-1.5 font-mono text-[9.5px] uppercase tracking-[0.14em] text-slate-500">Routes</div>
              {menu.index === 0 && (
                <Item icon="➕" label="Extend from beginning" onClick={() => editor.startExtend('start')} />
              )}
              {menu.index === pts.length - 1 && (
                <Item icon="➕" label="Extend from end" onClick={() => editor.startExtend('end')} />
              )}
              {canRemove
                ? <Item icon="✂️" label="Remove from route" onClick={() => editor.removePoint(menu.index)} />
                : <Item icon="⚠️" label="Min 2 waypoints" disabled />}
            </>
          )}
          <div className="my-1 border-t border-white/10" />
          <Item
            icon="🗑️" label="Delete waypoint" danger
            disabled={state.target?.type === 'route' && !canRemove}
            onClick={() => editor.removePoint(menu.index)}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- overlay

/** VN's RouteCreationOverlay, in the browser: same fields, same button rules. */
export function PlacementOverlay({ editor }: { editor: ChartEditor }) {
  const { state, stats, canFinishPlacing } = editor;
  const extending = state.mode === 'extend-start' || state.mode === 'extend-end';
  const waypoint = state.mode === 'draw-waypoint';

  return (
    // Solid background, not a translucent one: this panel carries text over
    // satellite imagery. It was `bg-[#101f30]/97`, which Tailwind never emitted
    // because 97 is not on its opacity scale — so the class vanished and the
    // overlay rendered completely transparent. Arbitrary colours take an
    // 8-digit hex if they ever need alpha; never a /NN off the scale.
    <div className="pointer-events-auto absolute bottom-4 left-1/2 z-[1100] flex -translate-x-1/2 flex-wrap items-center gap-4 rounded-xl border border-white/15 bg-[#101f30] px-4 py-3 shadow-2xl">
      {!extending && (
        <input
          type="text"
          value={state.drawName}
          onChange={(e) => editor.setDrawName(e.target.value)}
          placeholder={waypoint ? 'Waypoint name...' : 'Route name...'}
          className="w-48 rounded-lg border border-white/15 bg-[#0a1420] px-2.5 py-2 text-sm font-semibold text-slate-100 placeholder:text-slate-500 focus:border-sky-400 focus:outline-none"
        />
      )}
      <div className="text-center">
        <div className="font-mono text-lg font-semibold leading-none text-slate-100">{stats.count}</div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">{waypoint ? 'Placed' : 'Waypoints'}</div>
      </div>
      {!waypoint && (
        <div className="text-center">
          <div className="font-mono text-lg font-semibold leading-none text-slate-100">{stats.nm > 0 ? stats.nm.toFixed(1) : '0.0'}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">nm</div>
        </div>
      )}
      <p className="max-w-[190px] text-xs text-slate-300">
        {extending ? 'Click the map to extend the route' : 'Click on the map to add waypoints'}
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={editor.undoPlace} disabled={!state.placing.length}
          className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-100 disabled:opacity-40">Undo</button>
        <button type="button" onClick={editor.cancelPlacing}
          className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-100">Cancel</button>
        <button type="button" onClick={() => void editor.finishPlacing()} disabled={!canFinishPlacing}
          className="rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-slate-950 disabled:opacity-40">Finish</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- legs

/**
 * Leg-by-leg readout: bearing, distance, and a rename control.
 *
 * The pencil is here because right-clicking a waypoint on the chart was the
 * only way to rename one, and nothing on screen said so. An affordance that
 * exists but cannot be found is the same as one that does not exist.
 */
export function LegList({ editor, onRename }: { editor: ChartEditor; onRename?: (index: number) => void }) {
  const { state } = editor;
  if (!state.points.length) return null;
  return (
    <ul className="max-h-56 space-y-0.5 overflow-y-auto">
      {state.points.map((p, i) => {
        const next = state.points[i + 1];
        return (
          <li
            key={i}
            className={`flex items-center gap-1.5 rounded px-1.5 ${
              state.selected === i ? 'bg-white/10' : 'hover:bg-white/5'}`}
          >
            <button
              type="button"
              onClick={() => editor.select(i)}
              onDoubleClick={() => onRename?.(i)}
              title={onRename ? 'Click to highlight · double-click to rename' : undefined}
              className="flex min-w-0 flex-1 items-baseline justify-between gap-3 py-1.5 text-left"
            >
              <span className="truncate text-xs font-semibold text-slate-100">{p.name}</span>
              <span className="shrink-0 font-mono text-[10px] text-slate-400">
                {next ? `${String(Math.round(bearingDeg(p, next))).padStart(3, '0')}° ${distanceNm(p, next).toFixed(2)}` : '—'}
              </span>
            </button>
            {onRename && (
              <button
                type="button"
                onClick={() => onRename(i)}
                aria-label={`Rename ${p.name}`}
                title={`Rename ${p.name}`}
                className="shrink-0 rounded p-1 text-slate-500 hover:bg-white/10 hover:text-white"
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------- convert

/**
 * Track → route. The track is never touched; this previews the thinning and
 * writes a new route. Douglas–Peucker keeps the corners where the vessel
 * actually altered course, which is what makes the result readable as a plan.
 */
export function ConvertTrackModal({
  track, onCancel, onConvert,
}: {
  track: NavElement;
  onCancel: () => void;
  onConvert: (name: string, points: LatLon[]) => Promise<void>;
}) {
  const [target, setTarget] = useState(THIN_TARGET_DEFAULT);
  const [name, setName] = useState(`${track.name} (route)`);
  const [busy, setBusy] = useState(false);
  const thinned = useMemo(() => thinToTarget(track.points, target), [track.points, target]);

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-slate-900 p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white">Convert track to route</h3>
        <p className="mt-2 text-sm text-slate-400">
          “{track.name}” stays exactly as recorded. This creates a new route in your library
          that you can then edit.
        </p>

        <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Route name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1.5 w-full rounded-lg border border-white/15 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-sky-400 focus:outline-none"
        />

        <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
          Turning points
        </label>
        <div className="mt-2 flex items-center gap-3">
          <input
            type="range" min={THIN_TARGET_MIN} max={THIN_TARGET_MAX} value={target}
            onChange={(e) => setTarget(Number(e.target.value))}
            className="flex-1 accent-sky-500"
            aria-label="Target turning points"
          />
          <span className="w-28 shrink-0 text-right font-mono text-sm text-slate-200">
            {thinned.length} of {track.points.length.toLocaleString()}
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          The corners where you altered course are kept; the long straight runs between them are dropped.
        </p>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-200">Cancel</button>
          <button
            type="button"
            disabled={busy || !name.trim() || thinned.length < 2}
            onClick={async () => { setBusy(true); try { await onConvert(name.trim(), thinned); } finally { setBusy(false); } }}
            className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50"
          >
            {busy ? 'Converting…' : 'Create route'}
          </button>
        </div>
      </div>
    </div>
  );
}
